import React, {Fragment, useContext, useEffect, useState} from 'react';
import {Controller, useFormContext, useWatch} from 'react-hook-form';

import DatePicker from 'patient-ping-remedy/packages/date-picker';
import DropdownList from 'patient-ping-remedy/packages/dropdown-list';
import Spacer from 'patient-ping-remedy/packages/spacer';
import {sizes} from 'patient-ping-remedy/packages/theme';
import {CarecoApiContext} from "@/app-context/careco-api-context.tsx";
import {StyledDisclaimerText}
  from "@manual_encounters/encounter_admit/pings/AdmitHookForm.styles";
import {StyledRow} from "@manual_encounters/encounter_client_search/PatientInformationForm.styles";
import {DatePickerWrapper} from "@common/DatePickerWrapper.tsx";
import {log} from "@/helpers/logger.js";
import {DE_PROVIDERS_LABEL} from "@manual_encounters/constants/common";
import {useDeReferralAgencies} from "@/hooks/useDeReferralAgencies";

const requiredField = 'Required field';
const validateInputLength = (value) => {
  const trimmedValueLength = value?.label?.trim('')?.length;
  return trimmedValueLength > 0 && trimmedValueLength < 151;
};

const AdmitHookForm = ({ patient, isSnf, isEditing, groupId, isDeState, storedAgency = null }) => {
  const [settingOptions, setSettingOptions] = useState([]);
  const [showUpdateDisclaimer, setShowUpdateDisclaimer] = useState(false);
  const { carecoApi } = useContext(CarecoApiContext);
  const { agencyOptions, toAgencyOption } = useDeReferralAgencies();

  const { control, errors, formState, unregister } = useFormContext();
  const { dirtyFields } = formState;
  const admittedFrom = useWatch({ control, name: 'admittedFrom' });
  // Compares the label: edit and re-admit defaults carry only the label of DE Providers.
  const showDeAgency = isDeState && admittedFrom?.label === DE_PROVIDERS_LABEL;

  useEffect(function getSettingOptions() {
    carecoApi?.readSettings(groupId).then((options) => {
      setSettingOptions(options);
    });
    return () => {
      unregister('admittedFrom');
      unregister('date');
      unregister('setting');
      unregister('deAdmissionAgency');
    };
  }, []);

  useEffect(
    function scrollModalToDisclaimer() {
      if (showUpdateDisclaimer) {
        const disclaimer = document.getElementById('admitUpdateDisclaimer');
        disclaimer?.scrollIntoView({ behavior: 'smooth' });
      }
    },
    [showUpdateDisclaimer],
  );

  const checkDisclaimer = () => {
    setShowUpdateDisclaimer(dirtyFields?.setting);
  };

  const canShowDisclaimer = isSnf && isEditing && !!patient.currentPingEncounter;

  const handleSearch = async (inputValue) => {
    try {
      const facilities = await carecoApi?.searchFacilities(inputValue, undefined, groupId);
      const options = facilities.map((facility) => ({
        label: facility.label,
        value: facility.id,
      }));
      if (isDeState) {
        options.unshift({ label: 'DE Providers', value: 'DE_PROVIDERS' });
      }
      return options;
    } catch (error) {
      log('error', 'Error fetching facilities:', error);
      return [];
    }
  };

  return (
    <Fragment>
      <Controller
        name="admittedFrom"
        control={control}
        rules={{
          validate: validateInputLength,
        }}
        render={({ onChange, value, onBlur }) => (
          <DropdownList
            id="admittedFrom"
            label="Admitted From"
            value={value?.label ? value : null}
            handleChange={onChange}
            onBlur={onBlur}
            handleSearch={handleSearch}
            placeholder="Search..."
            isSearchable
            required
            error={!!errors.admittedFrom}
            errorMessage="Required field"
          />
        )}
      />
      {showDeAgency && (
        <Fragment>
          <Spacer itemHeight={sizes.medium} />
          <Controller
            name="deAdmissionAgency"
            control={control}
            defaultValue={storedAgency}
            rules={{ required: true }}
            render={({ onChange, onBlur, value }) => (
              <DropdownList
                id="deAdmissionAgency"
                label="DE Admission Agency"
                items={agencyOptions}
                value={toAgencyOption(value)}
                handleChange={(option) => onChange(option?.value ?? null)}
                onBlur={onBlur}
                isWithinModal
                required
                error={!!errors.deAdmissionAgency}
                errorMessage={requiredField}
              />
            )}
          />
        </Fragment>
      )}
      <Spacer itemHeight={sizes.medium} />
      <StyledRow>
        <Controller
          name="date"
          control={control}
          rules={{ required: true }}
          render={({ onChange, onBlur, value }) => (
            <DatePickerWrapper>
              <DatePicker
                onChange={onChange}
                onBlur={onBlur}
                maxDate={new Date()}
                value={value}
                externalError={errors.date}
                externalErrorText={requiredField}
                id="admitDate"
                labelText="Admitted Date"
                required="required"
              />
            </DatePickerWrapper>
          )}
        />
        {isSnf && (
          <Controller
            name="setting"
            control={control}
            rules={{ required: true }}
            render={({ onChange, onBlur, value }) => (
              <DropdownList
                handleChange={(event) => {
                  onChange(event.value);
                  if(canShowDisclaimer) checkDisclaimer();
                }}
                onBlur={onBlur}
                value={settingOptions.find((option) => option.value === value)}
                id="setting"
                items={settingOptions}
                label="Admitted To - Setting"
                isWithinModal
                required
                error={errors.setting}
                errorMessage={requiredField}
              />
            )}
          />
        )}
      </StyledRow>
      {showUpdateDisclaimer && (
        <StyledDisclaimerText id="admitUpdateDisclaimer">
          Are you trying to transfer this client? Please use the Update Client Status flow instead by clicking on the
          pencil on the client card. Editing the client status here will not produce a Ping for anyone on this
          client's care team.
        </StyledDisclaimerText>
      )}
    </Fragment>
  );
};

export default AdmitHookForm;
